import { ParsedUrlQuery } from "querystring";

import { FC, useEffect } from "react";
import { AsyncReturnType } from "type-fest";
import { GetServerSideProps } from "next";
import { Product } from "@prisma/client";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { getCookies, setCookie } from "@utils/cookies";
import { useHref } from "@hooks/use-href";
import Layout from "@components/common/layout";
import HighlightInformation from "@components/products/highlight-information";
import KeyDescriptions from "@components/products/key-descriptions";
import BreadCrumbs from "@components/common/breadcrumbs";
import AddToCartComponent from "@components/products/add-to-cart-component";
import ImageGallery from "@components/products/image-gallery";
import RelatedProducts from "@components/products/related-products";
import RecentlyViewedProducts from "@components/products/recently-viewed";
import TechnicalSpecifications from "@components/products/technical-specifications";
import ShareButtons from "@components/common/share-buttons";
import prisma, { getStaticData } from "@lib/prisma";
import { ProductWithSpecificationsAndDetails } from "@interfaces/product";
import { deserializeProduct, serializeProduct } from "@utils/serialize";
import ProductSlider from "@components/products/product-slider";
import { StaticProps } from "@interfaces/page";
import { renderMarkdown } from "@lib/markdown";
import { getParentCategories } from "@lib/category";
import OutOfStockComponent from "@components/products/out-of-stock-component";

interface Props extends StaticProps {
  product: ProductWithSpecificationsAndDetails;
  categories: AsyncReturnType<typeof getParentCategories>;
  collectionProducts: Product[];
  recentlyViewedProducts: Product[];
  similarProducts: Product[];
}

interface PathProps extends ParsedUrlQuery {
  slug: string;
}

const ProductPage: FC<Props> = ({
  product,
  categories,
  recentlyViewedProducts,
  collectionProducts,
  similarProducts,
}) => {
  const { t } = useTranslation("product");
  const { slug } = product;
  product = deserializeProduct(product);
  const { specificationPhoto, specifications, details } = product;
  const { locale } = useRouter();

  const href = useHref();

  useEffect(() => {
    if (!slug) {
      return;
    }

    const cookies = getCookies();

    const productHistory = cookies.productHistory ?? "";

    const productSlugs = [
      ...productHistory
        .split(",")
        .filter((productSlug) => productSlug && productSlug !== slug)
        .slice(-14),
      slug,
    ];

    setCookie("productHistory", productSlugs.toString(), {
      "max-age": 86400,
      path: "/",
    });
  }, [slug]);

  return (
    <Layout
      {...{
        title: product.name,
        description: product.description,
      }}
    >
      <BreadCrumbs
        breadcrumbs={[
          ...categories
            .map((c, i) => ({
              name: c.name,
              path: `${i === 0 ? "categories/" : ""}${c.slug}`,
            }))
            .reverse(),
          {
            name:
              locale === "en" ? product.category.nameEn : product.category.name,
            path: `categories/${product.category.slug}`,
          },
          product.name,
        ]}
      />
      <div className="text-black items-start flex flex-col-reverse md:flex-row">
        <div className="hidden md:block md:w-7/12">
          {product.photos && product.photos.length > 0 && (
            <ImageGallery {...product} />
          )}
        </div>
        <div className="w-full md:w-5/12 md:sticky top-0 flex flex-col px-2 space-y-4">
          <KeyDescriptions {...product} />
          <AddToCartComponent {...{ product }} />
          <OutOfStockComponent {...product} />
          {/* {product.category.onlinePurchase && product.numInStock !== 0 ? (
          ) : (
            <InquiryButton
              {...{
                code: product.pantheonCode,
                outOfStock: product.category.onlinePurchase,
              }}
            />
          )} */}
          <RelatedProducts
            products={collectionProducts.slice(4).map(deserializeProduct)}
          />
          <div className="w-full pt-4 pl-4 border-t">
            <ShareButtons url={href} />
          </div>
        </div>
      </div>
      <HighlightInformation {...{ details }} />
      <TechnicalSpecifications {...{ specifications, specificationPhoto }} />
      <ProductSlider
        title={t("same-collection")}
        products={collectionProducts.map(deserializeProduct)}
      />
      <ProductSlider
        title={t("similar")}
        products={similarProducts.map(deserializeProduct)}
      />
      <RecentlyViewedProducts
        products={recentlyViewedProducts.map(deserializeProduct)}
      />
    </Layout>
  );
};

export default ProductPage;

export const getServerSideProps: GetServerSideProps<Props, PathProps> = async ({
  req,
  params,
  locale,
}) => {
  const { cookies } = req ?? {};
  const { slug = "" } = params ?? {};

  const productHistory = cookies.productHistory ?? "";

  const recentlyViewedProductSlugs = productHistory
    .split(",")
    .filter((productSlug) => productSlug && productSlug !== slug)
    .slice(-14)
    .reverse();

  const product = await prisma.product.findUnique({
    where: { slug },
    include: {
      specifications: { orderBy: { order: "asc" } },
      details: true,
      category: true,
    },
  });

  if (!product) {
    return { notFound: true };
  }

  const [
    recentlyViewedProducts,
    similarProducts,
    collectionProducts,
    categories,
    staticData,
  ] = await getStaticData([
    prisma.product.findMany({
      where: { slug: { in: recentlyViewedProductSlugs }, published: true },
    }),
    prisma.product.findMany({
      where: {
        category: { slug: product.categorySlug },
        published: true,
      },
    }),
    prisma.product.findMany({
      where: {
        AND: {
          collectionId: product.collectionId,
          NOT: { collectionId: null },
        },
        published: true,
      },
    }),
    getParentCategories(product.categorySlug),
  ]);

  const serializedProduct = serializeProduct(
    product
  ) as ProductWithSpecificationsAndDetails;

  const translations = await serverSideTranslations(locale as string, [
    "common",
    "product",
  ]);

  return {
    props: {
      product: {
        ...serializedProduct,
        longDescription: renderMarkdown(serializedProduct.longDescription),
        longDescriptionEn: renderMarkdown(serializedProduct.longDescriptionEn),
        specifications: serializedProduct.specifications.map((spec) => ({
          ...spec,
          content: renderMarkdown(spec.content),
          contentEn: renderMarkdown(spec.contentEn),
        })),
      },
      categories,
      recentlyViewedProducts: recentlyViewedProducts
        .sort(
          ({ slug: slugA }, { slug: slugB }) =>
            recentlyViewedProductSlugs.indexOf(slugA) -
            recentlyViewedProductSlugs.indexOf(slugB)
        )
        .map(serializeProduct),
      similarProducts: similarProducts.map(serializeProduct),
      collectionProducts: collectionProducts.map(serializeProduct),
      staticData,
      ...translations,
    },
  };
};
